import { useState, useEffect } from 'react';
import type { GardenLayout } from '../types/gardenLayout';
import { MOCK_GARDEN_LAYOUTS } from '../data/gardenLayouts';

// Simulates an async fetch so this is easy to swap for a real Supabase query later.
// Replace the body of fetchLayouts() with a supabase.from('garden_layouts').select() call
// and remove the mock import when ready to connect real data.
function fetchLayouts(): Promise<GardenLayout[]> {
  const sorted = [...MOCK_GARDEN_LAYOUTS].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  return new Promise((resolve) => setTimeout(() => resolve(sorted), 300));
}

function fetchLayoutBySlug(slug: string): Promise<GardenLayout | null> {
  return new Promise((resolve) =>
    setTimeout(() => {
      const found = MOCK_GARDEN_LAYOUTS.find((l) => l.slug === slug) ?? null;
      resolve(found);
    }, 300)
  );
}

export function useGardenLayouts() {
  const [layouts, setLayouts] = useState<GardenLayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchLayouts()
      .then((data) => {
        if (!cancelled) setLayouts(data);
      })
      .catch(() => {
        if (!cancelled) setError('Unable to load garden layouts. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { layouts, loading, error };
}

export function useGardenLayout(slug: string | undefined) {
  const [layout, setLayout] = useState<GardenLayout | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      setError('Layout not found');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchLayoutBySlug(slug)
      .then((data) => {
        if (!cancelled) {
          setLayout(data);
          if (!data) setError('Layout not found');
        }
      })
      .catch(() => {
        if (!cancelled) setError('Unable to load this layout. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { layout, loading, error };
}
